// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getCustomerCreditData from "@bg/App/Handlers/CustomerCreditCheckHandler/getCustomerCredit";
import { CustomAppState, xHeaderActions } from "@bg/App/Store/Reducers";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Modal from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import React, { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

const enum fields {}

interface CustomerCreditCheckWidgetProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const currentPage = getCurrentPage(state).type; // ChangeCustomerPage

    return {
        creditCheckModalDidOpen: state.xHeader.creditCheckModalDidOpen,
        creditCheckCallIsSubmitted: state.xHeader.creditCheckCallIsSubmitted,
        session: getSession(state),
        changeCustomerPage: getPageLinkByPageType(state, "ChangeCustomerPage"),
        currentPage,
    };
};

const mapDispatchToProps = {
    getCustomerCreditData,
};

type Props = CustomerCreditCheckWidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext;

const CustomerCreditCheckWidget: React.FC<Props> = props => {
    const dispatch = useDispatch();

    const {
        shellContext,
        creditCheckModalDidOpen,
        creditCheckCallIsSubmitted,
        session,
        getCustomerCreditData,
        currentPage,
        changeCustomerPage,
    } = props;

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [messageIsSoft, setMessageIsSoft] = React.useState(false);
    const [messageIsHard, setMessageIsHard] = React.useState(false);

    React.useEffect(() => {
        if (
            !creditCheckModalDidOpen &&
            !creditCheckCallIsSubmitted &&
            session.isAuthenticated &&
            session.billToId &&
            currentPage !== changeCustomerPage?.type
        ) {
            dispatch({ type: "xHeader/creditCheckCallIsSubmitted", creditCheckCallIsSubmitted: true });
            setTimeout(() => {
                getCustomerCreditData({
                    onSuccess: message => {
                        if (!getCookie("creditCheck")) {
                            if (message.showHardHoldWarningMessage === true) {
                                setModalIsOpen(true);
                                setMessageIsSoft(false);
                                setMessageIsHard(true);
                                dispatch({ type: "xHeader/creditCheckModalDidOpen", creditCheckModalDidOpen: true });
                            } else if (message.showSoftHoldWarningMessage === true) {
                                setModalIsOpen(true);
                                setMessageIsSoft(true);
                                setMessageIsHard(false);
                                dispatch({ type: "xHeader/creditCheckModalDidOpen", creditCheckModalDidOpen: true });
                            } else {
                                setModalIsOpen(false);
                                setMessageIsSoft(false);
                                setMessageIsHard(false);
                                dispatch({ type: "xHeader/creditCheckModalDidOpen", creditCheckModalDidOpen: false });
                            }
                        }
                    },
                    onError: () => {
                        if (!getCookie("creditCheck")) {
                            dispatch({ type: "xHeader/creditCheckCallIsSubmitted", creditCheckCallIsSubmitted: false });
                        }
                    },
                });
            }, 50);
        }
    }, []);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        const today = new Date();
        const nextDay = new Date(new Date().setDate(today.getDate() + 1));
        setCookie("creditCheck", "true", { expires: nextDay });
    };

    return (
        <>
            <Modal
                size={630}
                headline={siteMessage("Custom_CreditCheckModalTitle")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <StyledWrapper>
                    <Typography>
                        {messageIsSoft
                            ? siteMessage("Custom_CreditCheckMessageSoft")
                            : messageIsHard
                            ? siteMessage("Custom_CreditCheckMessageHard")
                            : translate("Something Wrong Happened, Please try again later!")}
                    </Typography>
                    <StyledWrapper className="modal-actions">
                        <Button onClick={modalCloseHandler}>{translate("Close")}</Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export const definition: WidgetDefinition = {
    group: "XNGAGE" as any,
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(CustomerCreditCheckWidget)),
    definition,
};

export default widgetModule;
